import { captureException } from '@common/sentry';
import { ErrorTags, SentryTags } from '@common/sentry/SentryTags';
import { NestedData } from '@prophecy/interfaces/generic';
import { ProphecyError } from '@prophecy/utils/error';
import { getTabId } from '@prophecy/utils/session';

import { csrf } from '../../utils/csrf';
import { replayToken } from '../../utils/replayToken';
import { shareToken } from '../../utils/shareToken';
import { TAB_ID_HEADER } from '../../utils/tabId';

export async function fetchURL(
  url: URL | RequestInfo,
  requestConfig: RequestInit | undefined,
  handleUnAuthorizedCall: (status: number, url: string) => boolean = () => true,
  excludeShareTokens?: boolean
) {
  const body = requestConfig?.body;
  const isFormData = body instanceof FormData;
  const requestHeaders: NestedData = {
    accept: 'application/json;',
    'Content-Type': 'application/json;charset=utf-8',
    [csrf.key]: csrf.get(),
    [TAB_ID_HEADER]: getTabId(),
    ...requestConfig?.headers
  };
  // attach share-token for API calls only
  const shareTokenValue = shareToken.get?.();
  const replayTokenValue = replayToken.get?.();
  const isApiCall = url.toString().includes('/api/');
  if (shareTokenValue && isApiCall && !excludeShareTokens) {
    (requestHeaders as Record<string, string>)[shareToken.key] = shareTokenValue;
  }
  if (replayTokenValue && isApiCall && !excludeShareTokens) {
    (requestHeaders as Record<string, string>)[replayToken.key] = replayTokenValue;
  }
  if (isFormData && body) {
    delete requestHeaders['Content-Type'];
  }
  // add csrf token and credentials
  const modifiedOptions: RequestInit = {
    credentials: 'include',
    ...requestConfig,
    headers: { [csrf.key]: csrf.get(), ...requestHeaders }
  };

  let response: Response | undefined;

  try {
    response = await fetch(url, modifiedOptions);
  } catch (e) {
    // Report the real network failure (groups by actual cause/host). The generic wrapper below is
    // report:false so failures don't also collapse into one "Something went wrong in an API" group.
    captureException({
      exception: e,
      errorTags: { [SentryTags.ProphecyErrorType]: ErrorTags.NetworkError }
    });
    throw new ProphecyError('Something went wrong in an API', { report: false });
  }

  const isAuthorized = handleUnAuthorizedCall(response?.status, url.toString?.());

  if (!response?.ok && isAuthorized) {
    // anything outside 200 range
    let errorResponse;
    const clonedResponse = response?.clone();
    try {
      errorResponse = await response?.json();
    } catch {
      // Non-JSON error body. Surface a short plaintext message to the user (e.g. a 4xx reason), but
      // fall back to the status for empty/HTML bodies so we don't dump an error page into the toast
      // or fragment Sentry grouping.
      const body = (await clonedResponse?.text())?.trim();
      const message =
        body && body.length <= 200 && !body.startsWith('<') ? body : `Something went wrong (${response?.status})`;
      throw new ProphecyError(message, { cause: response?.status?.toString() });
    }
    throw errorResponse;
  }
  // TODO: remove below logic once backend starts sending right content-type header.
  // Only coerce successful responses — forcing application/json on a plaintext/HTML error body
  // makes JSON parsers (e.g. graphql-request) throw SyntaxError on the unauthenticated path.
  const headers: [string, string][] = [];
  for (const [key, value] of response.headers.entries()) {
    if (key === 'content-type' && response.ok) {
      headers.push([key, 'application/json']);
    } else {
      headers.push([key, value]);
    }
  }
  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers
  });
}
